import { createSlice, current } from '@reduxjs/toolkit';
import {
	getBreachCategoryList,
	getBreachCategory,
	addNewBreachCategory,
	updateBreachCategory,
	deleteBreachCategory,
	checkBreachCategoryExists,
} from './thunk';

const getOptionLabel = item => item?.name || item?.label || item?.description || item?._id || '';

export const initialState = {
	breachCategoryList: [],
	breachCategoryListSelect: [],
	isLoadingList: false,
	isSuccess: false,
	isAdding: false,
	isAdded: false,
	isAddError: false,
	isUpdating: false,
	isUpdated: false,
	isUpdateError: false,
	isDeleting: false,
	isDeleted: false,
	isDeleteError: false,
	requireCloseModal: false,
	exists: false,
};

const BreachCategorySlice = createSlice({
	name: 'BreachCategory',
	initialState,
	reducers: {
		setRequireCloseModal(state, action) {
			state.requireCloseModal = action.payload;
		},
	},
	extraReducers: builder => {
		// List
		builder.addCase(getBreachCategoryList.pending, state => {
			state.isLoadingList = true;
			state.isSuccess = false;
		});

		builder.addCase(getBreachCategoryList.fulfilled, (state, action) => {
			const list = action.payload || [];
			state.breachCategoryList = list;
			state.breachCategoryListSelect = list.map(item => ({
				item,
				value: item._id,
				label: getOptionLabel(item),
			}));
			state.isLoadingList = false;
			state.isSuccess = true;
		});

		builder.addCase(getBreachCategoryList.rejected, (state, action) => {
			state.error = action.payload || null;
			state.isLoadingList = false;
			state.isSuccess = false;
		});

		// Add
		builder.addCase(addNewBreachCategory.pending, state => {
			state.isAdding = true;
			state.isAdded = false;
			state.isAddError = false;
		});

		builder.addCase(addNewBreachCategory.fulfilled, (state, action) => {
			state.breachCategoryList.push(action.payload);
			state.breachCategoryListSelect.push({
				item: action.payload,
				value: action.payload._id,
				label: getOptionLabel(action.payload),
			});
			state.isAdding = false;
			state.isAdded = true;
			state.requireCloseModal = true;
		});

		builder.addCase(addNewBreachCategory.rejected, (state, action) => {
			state.error = action.payload || null;
			state.isAdding = false;
			state.isAddError = true;
		});

		// Update
		builder.addCase(updateBreachCategory.pending, state => {
			state.isUpdating = true;
			state.isUpdated = false;
			state.isUpdateError = false;
		});

		builder.addCase(updateBreachCategory.fulfilled, (state, action) => {
			state.breachCategoryList = state.breachCategoryList.map(item =>
				item._id.toString() === action.payload._id.toString()
					? { ...item, ...action.payload }
					: item
			);
			state.breachCategoryListSelect = state.breachCategoryListSelect.map(opt =>
				opt.value.toString() === action.payload._id.toString()
					? {
						item: action.payload,
						value: action.payload._id,
						label: getOptionLabel(action.payload),
					}
					: opt
			);
			state.isUpdating = false;
			state.isUpdated = true;
			state.requireCloseModal = true;
		});

		builder.addCase(updateBreachCategory.rejected, (state, action) => {
			state.error = action.payload || null;
			state.isUpdating = false;
			state.isUpdateError = true;
		});

		// Delete
		builder.addCase(deleteBreachCategory.pending, state => {
			state.isDeleting = true;
			state.isDeleted = false;
			state.isDeleteError = false;
		});

		builder.addCase(deleteBreachCategory.fulfilled, (state, action) => {
			state.breachCategoryList = current(state.breachCategoryList).filter(
				item => item._id !== action.payload._id
			);
			state.breachCategoryListSelect = current(
				state.breachCategoryListSelect
			).filter(opt => opt.value !== action.payload._id);
			state.isDeleting = false;
			state.isDeleted = true;
			state.requireCloseModal = true;
		});

		builder.addCase(deleteBreachCategory.rejected, (state, action) => {
			state.error = action.payload || null;
			state.isDeleting = false;
			state.isDeleteError = true;
		});

		// Check exists
		builder.addCase(checkBreachCategoryExists.pending, state => {
			state.exists = false;
		});

		builder.addCase(checkBreachCategoryExists.fulfilled, (state, action) => {
			state.exists = action.payload.exists;
		});

		builder.addCase(checkBreachCategoryExists.rejected, state => {
			state.exists = false;
		});
	},
});

export const { setRequireCloseModal } = BreachCategorySlice.actions;

export default BreachCategorySlice.reducer;
